import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDataTable } from '../../../hooks';
import { isEmpty } from '../../../utils/helpers';
import LoadingSpinner from '../../ui/LoadingSpinner';

/**
 * DataTable component for displaying tabular data with sorting, filtering, and pagination
 */
const DataTable = ({
  data = [],
  columns = [],
  title = '',
  initialSort = null,
  initialSortDirection = 'asc',
  initialFilters = {},
  searchFields = [],
  searchPlaceholder = 'Search...',
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultRowsPerPage = 10,
  loading = false,
  error = null,
  onRowClick = null,
  emptyStateMessage = 'No data to display',
  showToolbar = true,
  showPagination = true,
  showSearch = true,
  showRefresh = false,
  onRefresh = null,
  getRowId = (row) => row.id,
  getRowProps = () => ({}),
  stickyHeader = true,
  maxHeight = '70vh',
  paperProps = {},
  tableProps = {},
  headerCellProps = {},
  bodyCellProps = {},
  rowProps = {},
  rowHoverEffect = false,
}) => {
  // Set up data table with hooks
  const {
    items,
    tableInfo,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleSearch,
    clearFilters,
    resetTable,
  } = useDataTable(data, {
    initialPage: 1,
    initialPageSize: defaultRowsPerPage,
    initialSortField: initialSort,
    initialSortDirection,
    initialFilters,
    searchFields,
  });
  
  // Memoize the search handler to avoid unnecessary re-renders
  const handleSearchChange = useMemo(() => {
    return (e) => {
      handleSearch(e.target.value);
    };
  }, [handleSearch]);
  
  // Handle page change
  const onPageChange = (event, newPage) => {
    handlePageChange(newPage + 1); // Our hook uses 1-based indexing, MUI uses 0-based
  };
  
  // Handle rows per page change
  const onRowsPerPageChange = (event) => {
    handlePageSizeChange(parseInt(event.target.value, 10));
  };
  
  // Handle refresh
  const handleRefresh = () => {
    resetTable();
    if (onRefresh) {
      onRefresh();
    }
  };
  
  // Handle row click
  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };
  
  // Render table header
  const renderTableHeader = () => {
    return (
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell
              key={column.field}
              align={column.align || 'left'}
              padding={column.padding || 'normal'}
              sortDirection={tableInfo.field === column.field ? tableInfo.direction : false}
              style={{
                minWidth: column.minWidth,
                width: column.width,
                ...headerCellProps,
              }}
            >
              {column.sortable !== false ? (
                <TableSortLabel
                  active={tableInfo.field === column.field}
                  direction={tableInfo.field === column.field ? tableInfo.direction : 'asc'}
                  onClick={() => handleSortChange(column.field)}
                  IconComponent={tableInfo.direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                >
                  {column.headerName}
                </TableSortLabel>
              ) : (
                column.headerName
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };
  
  // Render table body
  const renderTableBody = () => {
    if (loading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} align="center" padding="normal">
              <LoadingSpinner size={40} />
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }
    
    if (error) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} align="center" padding="normal">
              <Typography color="error" variant="body1">
                {error}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }
    
    if (isEmpty(items)) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} align="center" padding="normal">
              <Typography variant="body1" color="textSecondary">
                {emptyStateMessage}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }
    
    return (
      <TableBody>
        {items.map((row) => {
          const rowId = getRowId(row);
          return (
            <TableRow
              hover
              key={rowId}
              onClick={() => handleRowClick(row)}
              sx={{ 
                cursor: onRowClick ? 'pointer' : 'default',
                transition: rowHoverEffect ? 'all 0.2s ease' : 'none',
                ...(rowHoverEffect && {
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  }
                })
              }}
              {...getRowProps(row)}
              {...rowProps}
            >
              {columns.map((column) => {
                const value = column.valueGetter
                  ? column.valueGetter(row)
                  : row[column.field];
                
                return (
                  <TableCell
                    key={`${rowId}-${column.field}`}
                    align={column.align || 'left'}
                    padding={column.padding || 'normal'}
                    {...bodyCellProps}
                  >
                    {column.renderCell ? column.renderCell(value, row) : value}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    );
  };
  
  // Render table toolbar
  const renderToolbar = () => {
    if (!showToolbar) return null;
    
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Box sx={{ mb: { xs: 1, sm: 0 } }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {tableInfo.hasActiveFilters && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
              <Chip
                size="small"
                label={`${tableInfo.activeFilterCount} filter${
                  tableInfo.activeFilterCount !== 1 ? 's' : ''
                } active`}
                onDelete={clearFilters}
                color="primary"
                icon={<FilterListIcon />}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="textSecondary">
                {tableInfo.filteredItemCount} of {tableInfo.totalItems} items
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {showSearch && searchFields.length > 0 && (
            <TextField
              variant="outlined"
              size="small"
              placeholder={searchPlaceholder}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: tableInfo.hasActiveFilters ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={clearFilters}
                      edge="end"
                      aria-label="clear search"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mr: 2 }}
            />
          )}
          
          {showRefresh && (
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };
  
  // Render pagination
  const renderPagination = () => {
    if (!showPagination) return null;
    
    return (
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={tableInfo.filteredItemCount}
        rowsPerPage={tableInfo.pageSize}
        page={tableInfo.currentPage - 1} // Convert 1-based to 0-based for MUI
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    );
  };
  
  return (
    <Paper
      elevation={1}
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...paperProps,
      }}
    >
      {renderToolbar()}
      
      <TableContainer
        sx={{
          maxHeight: stickyHeader ? maxHeight : 'none',
        }}
      >
        <Table
          stickyHeader={stickyHeader}
          aria-label={title || 'data-table'}
          {...tableProps}
        >
          {renderTableHeader()}
          {renderTableBody()}
        </Table>
      </TableContainer>
      
      {renderPagination()}
    </Paper>
  );
};

DataTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      align: PropTypes.oneOf(['left', 'right', 'center']),
      padding: PropTypes.oneOf(['normal', 'checkbox', 'none']),
      sortable: PropTypes.bool,
      valueGetter: PropTypes.func,
      renderCell: PropTypes.func,
    })
  ).isRequired,
  title: PropTypes.string,
  initialSort: PropTypes.string,
  initialSortDirection: PropTypes.oneOf(['asc', 'desc']),
  initialFilters: PropTypes.object,
  searchFields: PropTypes.arrayOf(PropTypes.string),
  searchPlaceholder: PropTypes.string,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  defaultRowsPerPage: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRowClick: PropTypes.func,
  emptyStateMessage: PropTypes.string,
  showToolbar: PropTypes.bool,
  showPagination: PropTypes.bool,
  showSearch: PropTypes.bool,
  showRefresh: PropTypes.bool,
  onRefresh: PropTypes.func,
  getRowId: PropTypes.func,
  getRowProps: PropTypes.func,
  stickyHeader: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  paperProps: PropTypes.object,
  tableProps: PropTypes.object,
  headerCellProps: PropTypes.object,
  bodyCellProps: PropTypes.object,
  rowProps: PropTypes.object,
  rowHoverEffect: PropTypes.bool,
};

export default DataTable;
